import { Check, ChevronRight, CircleHelp, Copy, Link2, RefreshCw, ShieldCheck, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { calculateOptionScores, normalizeCriteriaWeights, recommendationConfidence } from "../shared/decision-engine";
import type { DecisionReport } from "../shared/types";
import { createShare } from "./api";
import { DecisionScale } from "./DecisionScale";

interface ReportViewProps {
  report: DecisionReport;
  onChange?: (report: DecisionReport) => void;
  onRestart?: () => void;
  shared?: boolean;
  privateAnswers?: string[];
}

export function ReportView({ report, onChange, onRestart, shared = false, privateAnswers = [] }: ReportViewProps) {
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareError, setShareError] = useState("");
  const ranked = useMemo(() => calculateOptionScores(report.criteria, report.options), [report]);
  const confidence = recommendationConfidence(ranked);

  const updateWeight = (criterionId: string, weight: number) => {
    if (!onChange) return;
    const raw = report.criteria.map((criterion) => criterion.id === criterionId ? { ...criterion, weight } : criterion);
    onChange({ ...report, criteria: normalizeCriteriaWeights(raw), confidence });
  };

  const updateScore = (optionId: string, criterionId: string, score: number) => {
    if (!onChange) return;
    onChange({
      ...report,
      options: report.options.map((option) => option.id === optionId
        ? { ...option, scores: { ...option.scores, [criterionId]: score } }
        : option),
      confidence,
    });
  };

  const share = async () => {
    setSharing(true);
    setShareError("");
    try {
      const result = await createShare(report, privateAnswers);
      setShareUrl(result.url);
      await navigator.clipboard?.writeText(result.url);
    } catch (error) {
      setShareError(error instanceof Error ? error.message : "未能生成分享連結");
    } finally {
      setSharing(false);
    }
  };

  return (
    <main className="report-shell">
      <header className="report-head">
        <span className="eyebrow">{shared ? "一份公開的決策快照" : "你的決策整理好了"}</span>
        <h1>{report.title}</h1>
        <p>{report.summary}</p>
      </header>

      <div className="report-grid">
        <section className="verdict-card">
          <div>
            <span className="section-label">目前判斷</span>
            <h2>{report.recommendation}</h2>
          </div>
          <div className={`confidence confidence--${confidence}`}>
            <span>信心</span>
            <strong>{confidence === "high" ? "較高" : confidence === "medium" ? "中等" : "偏低"}</strong>
          </div>
        </section>
        <DecisionScale criteria={report.criteria} options={report.options} />
      </div>

      <section className="matrix-section">
        <div className="section-heading">
          <div><span className="section-label">決策矩陣</span><h2>把在意的事放上桌</h2></div>
          {!shared && <p>可直接調整；總權重會自動保持 100%</p>}
        </div>
        <div className="matrix-wrap">
          <table className="matrix">
            <thead><tr><th>評估因素</th><th>權重</th>{report.options.map((option) => <th key={option.id}>{option.label}</th>)}</tr></thead>
            <tbody>
              {report.criteria.map((criterion) => (
                <tr key={criterion.id}>
                  <th>{criterion.label}</th>
                  <td>
                    {shared ? <strong>{criterion.weight}%</strong> : <label className="range-cell"><input aria-label={`${criterion.label}權重`} type="range" min="1" max="80" value={criterion.weight} onChange={(event) => updateWeight(criterion.id, Number(event.target.value))} /><span>{criterion.weight}%</span></label>}
                  </td>
                  {report.options.map((option) => <td key={option.id}>{shared ? <strong>{option.scores[criterion.id] ?? 0}</strong> : <input className="score-input" aria-label={`${option.label}在${criterion.label}的分數`} type="number" min="0" max="10" value={option.scores[criterion.id] ?? 0} onChange={(event) => updateScore(option.id, criterion.id, Number(event.target.value))} />}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="insight-grid">
        <section className="insight-card insight-card--warning"><TriangleAlert size={21} /><div><span className="section-label">可能的盲點</span><ul>{report.blindSpots.map((item) => <li key={item}>{item}</li>)}</ul></div></section>
        <section className="insight-card"><CircleHelp size={21} /><div><span className="section-label">仍要確認</span><ul>{report.openQuestions.map((item) => <li key={item}>{item}</li>)}</ul></div></section>
      </div>

      <section className="scenarios">
        <div className="section-heading"><div><span className="section-label">情境預演</span><h2>如果事情這樣發展</h2></div></div>
        <div className="scenario-grid">
          <article><span>順利時</span><p>{report.scenarios.optimistic}</p></article>
          <article><span>更可能</span><p>{report.scenarios.likely}</p></article>
          <article><span>不如預期</span><p>{report.scenarios.downside}</p></article>
        </div>
      </section>

      <section className="next-card">
        <div><span className="section-label">別再繼續空想</span><h2>下一步，只做這三件事</h2></div>
        <ol>{report.nextSteps.map((step) => <li key={step}><Check size={17} /><span>{step}</span></li>)}</ol>
      </section>

      <blockquote className="coach-note">“{report.coachNote}”<cite>— 定見教練</cite></blockquote>

      {!shared && <section className="share-panel">
        <div><ShieldCheck size={22} /><div><strong>只分享這份報告</strong><p>原始回答和本機草稿不會上傳；分享連結 30 天後失效。</p></div></div>
        <button className="button button--dark" onClick={share} disabled={sharing}>{sharing ? "正在生成…" : shareUrl ? <><Copy size={17} />已複製連結</> : <><Link2 size={17} />生成分享連結</>}</button>
        {shareUrl && <a href={shareUrl}>{shareUrl}<ChevronRight size={15} /></a>}
        {shareError && <p className="error-text">{shareError}</p>}
      </section>}

      <div className="report-actions">
        {onRestart && <button className="text-button" onClick={onRestart}><RefreshCw size={16} />開始另一個決定</button>}
      </div>
    </main>
  );
}
